import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Moon, Search, Sun } from "lucide-react";
import { getText } from "../i18n";
import { getMenuData } from "./Menu";

const flattenMenu = (items, parent = "") =>
  items.flatMap((item) => {
    if (item.isTitle) return [];
    const group = parent || item.label;
    if (item.children?.length) {
      return flattenMenu(item.children, item.label);
    }
    return item.href ? [{ ...item, group }] : [];
  });

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const Navbar = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const t = getText("pt-AO", "common");
  const [theme, setTheme] = useState(localStorage.getItem("facturize_theme") || "light");
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const searchItems = useMemo(() => flattenMenu(getMenuData()), []);
  const results = useMemo(() => {
    const term = normalize(query.trim());
    if (!term) return [];
    return searchItems
      .filter((item) => normalize(`${item.label} ${item.group} ${item.href}`).includes(term))
      .slice(0, 7);
  }, [query, searchItems]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("facturize_theme", theme);
  }, [theme]);

  const toggleSidebar = () => {
    document.body.classList.toggle("sidebar-enable");
  };

  const openResult = (href) => {
    setQuery("");
    setFocused(false);
    navigate(href);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    if (results[0]) openResult(results[0].href);
  };

  return (
    <header className="app-header facturize-topbar" id="appHeader">
      <div className="container-fluid">
        <div className="topbar-grid">
          <div className="d-flex align-items-center gap-2">
            <button className="icon-btn d-lg-none" type="button" onClick={toggleSidebar} aria-label="Menu">
              <Menu size={19} />
            </button>
            <div className="cert-pill">
              <span>{t.certification}</span>
              <strong>FE/238/AGT/2026</strong>
            </div>
          </div>

          <form className="topbar-search-wrap" onSubmit={submitSearch}>
            <label className="topbar-search" aria-label={t.search}>
              <Search size={16} />
              <input
                type="search"
                value={query}
                placeholder={`${t.search} modulo, pagina ou relatorio...`}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 160)}
              />
            </label>

            {focused && query.trim() && (
              <div className="topbar-search-results">
                {results.length ? (
                  results.map((item) => (
                    <button key={`${item.href}-${item.label}`} type="button" onClick={() => openResult(item.href)}>
                      <span>{item.label}</span>
                      <small>{item.group}</small>
                    </button>
                  ))
                ) : (
                  <div className="search-empty">Nenhum resultado encontrado.</div>
                )}
              </div>
            )}
          </form>

          <div className="topbar-actions">
            <button
              className="icon-btn"
              type="button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              title={theme === "dark" ? t.lightMode : t.darkMode}
              aria-label={theme === "dark" ? t.lightMode : t.darkMode}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div className="dropdown">
              <button className="profile-chip" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                <span className="profile-copy">
                  <strong>{user?.nome || t.user}</strong>
                  <small>{user?.nivel || t.access}</small>
                </span>
                <img src={user?.foto || "https://via.placeholder.com/40"} width={38} height={38} alt={t.user} />
              </button>

              <div className="dropdown-menu dropdown-menu-end header-dropdown-menu p-3 shadow-lg border-0">
                <div className="border-bottom pb-2 mb-2 d-flex align-items-center gap-2">
                  <img src={user?.foto || "https://via.placeholder.com/40"} alt={t.user} className="avatar-md rounded-circle" width={40} height={40} />
                  <div>
                    <h6 className="mb-0 fs-14">{user?.nome || t.user}</h6>
                    <p className="mb-0 fs-12 text-muted text-truncate" style={{ maxWidth: "150px" }}>{user?.email}</p>
                  </div>
                </div>
                <Link className="dropdown-item rounded-2 py-2" to="/settings">
                  <i className="bi bi-gear me-2 text-secondary" /> {t.settings}
                </Link>
                <hr className="dropdown-divider" />
                <Link className="dropdown-item rounded-2 py-2 text-danger" to="/auth/logout">
                  <i className="bi bi-box-arrow-right me-2" /> {t.logout}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
